import {
  type EventSourceMessage,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { useEffect, useRef } from "react";

import { config } from "@/config";
import { useAuth } from "@/hooks/use-auth";
import { useAppDispatch } from "@/store/useApp";
import { DocumentStatus } from "@/types/document";

interface DocumentEventPayload {
  progress?: { current: number; total: number };
  status?: DocumentStatus;
}

interface DocumentServerEventData {
  document_id?: string;
  payload?: DocumentEventPayload;
}

interface UseServerEventsOptions {
  enabled: boolean;
  onRefreshDocuments: () => Promise<void>;
  onRefreshAssessments: () => Promise<void>;
}

const SERVER_EVENTS = {
  DOCUMENT_PROGRESS: "document.progress",
  DOCUMENT_READY: "document.ready",
  TRANSCRIPT_PROCESSED: "transcript.processed",
  FEEDBACK_PUBLISHED: "feedback.published",
} as const;

export const useServerEvents = ({
  enabled,
  onRefreshDocuments,
  onRefreshAssessments,
}: UseServerEventsOptions) => {
  const { getAccessToken, logout, refreshAccessToken } = useAuth();
  const dispatch = useAppDispatch();

  const getAccessTokenRef = useRef(getAccessToken);
  const refreshAccessTokenRef = useRef(refreshAccessToken);
  const onRefreshDocumentsRef = useRef(onRefreshDocuments);
  const onRefreshAssessmentsRef = useRef(onRefreshAssessments);
  const dispatchRef = useRef(dispatch);
  const logoutRef = useRef(logout);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  useEffect(() => {
    refreshAccessTokenRef.current = refreshAccessToken;
  }, [refreshAccessToken]);

  useEffect(() => {
    onRefreshDocumentsRef.current = onRefreshDocuments;
  }, [onRefreshDocuments]);

  useEffect(() => {
    onRefreshAssessmentsRef.current = onRefreshAssessments;
  }, [onRefreshAssessments]);

  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    if (!enabled) {
      controllerRef.current?.abort();
      controllerRef.current = null;
      return;
    }

    if (controllerRef.current) {
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    const parseEventData = (data: string) => {
      try {
        return JSON.parse(data) as DocumentServerEventData;
      } catch (error) {
        console.error("Failed to parse SSE message", error);
        return null;
      }
    };

    const handleMessage = (event: EventSourceMessage) => {
      switch (event.event) {
        case SERVER_EVENTS.DOCUMENT_PROGRESS: {
          const data = parseEventData(event.data);
          if (!data?.document_id || !data.payload?.progress) return;
          
          dispatchRef.current({
            type: "UPDATE_DOCUMENT_PROGRESS",
            payload: { id: data.document_id, progress: data.payload.progress },
          });
          return;
        }
        case SERVER_EVENTS.DOCUMENT_READY:
        case SERVER_EVENTS.TRANSCRIPT_PROCESSED:
          void onRefreshDocumentsRef.current();
          void onRefreshAssessmentsRef.current();
          return;
        case SERVER_EVENTS.FEEDBACK_PUBLISHED:
          void onRefreshAssessmentsRef.current();
          return;
        default:
          return;
      }
    };

    const connect = async () => {
      try {
        await fetchEventSource(`${config.apiUrl}/events`, {
          method: "GET",
          signal: controller.signal,
          openWhenHidden: true,
          fetch: async (input, init) => {
            const makeRequest = async (token: string) => {
              return await fetch(input, {
                ...init,
                headers: {
                  ...(init?.headers || {}),
                  Authorization: `Bearer ${token}`,
                },
              });
            };

            let response = await makeRequest(await getAccessTokenRef.current());

            if (response.status === 401 || response.status === 403) {
              try {
                response = await makeRequest(await refreshAccessTokenRef.current());
              } catch {
                logoutRef.current();
                controller.abort();
                return response;
              }
            }
            if (response.status === 401 || response.status === 403) {
              logoutRef.current();
              controller.abort();
            }
            return response;
          },
          onmessage: handleMessage,
          onerror(error) {
            if (controller.signal.aborted) return;
            console.error("SSE error", error);
            return 1500;
          },
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Failed to establish SSE connection", error);
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
        }
      }
    };

    void connect();

    return () => {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
      controller.abort();
    };
  }, [enabled]);
};
